import { DateObject, useIntl } from '@capsicum/common';
import { type FanTokenOfferingWithPartner, useMainCurrenciesQuery } from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ClaimableFTOAtom } from '../data-access';
import { FanTokenOfferingsPage, FTOCardStatus } from '../models';
import { getPartnerLogo, getPeggedAmount } from '../util';

import { useFanTokenOfferingRibbonColor } from './useFanTokenOfferingRibbonColor';

export type FanTokenOfferingCardStatus =
	| 'aboutToStart'
	| 'claimable'
	| 'delayedClaimPeriod'
	| 'endedClaimPeriod'
	| 'finishedWithoutPurchase'
	| 'opened'
	| 'published'
	| 'waitingForClaimPeriodToStart'
	| null;

export const useFanTokenOfferingCard = ({
	fto,
	ftoIndex,
	onElapsed,
	page
}: {
	fto: FanTokenOfferingWithPartner;
	ftoIndex: number;
	onElapsed: () => void;
	page: FanTokenOfferingsPage;
}) => {
	const { $t } = useIntl();
	const { data: walletCurrencies } = useMainCurrenciesQuery(['crypto', 'fiat']);
	const defaultCurrencyId = walletCurrencies?.defaultCurrency?.id ?? '';
	const defaultCurrencyShortCode = walletCurrencies?.defaultCurrency?.shortCode ?? '';
	const altCurrencyId = walletCurrencies?.altCurrency?.id ?? '';
	const altCurrencyShortCode = walletCurrencies?.altCurrency?.shortCode ?? '';

	const setClaimableFTO = useSetAtom(ClaimableFTOAtom);
	const [starting, setStarting] = useState(false);
	const [currentTime, setCurrentTime] = useState(() => new DateObject());
	const isFanTokenOfferingHome = useMemo(() => page === FanTokenOfferingsPage.Home, [page]);
	const logoSrc = useMemo(() => getPartnerLogo(fto.fanToken.partner.images), [fto.fanToken.partner.images]);
	const startDate = useMemo(() => new DateObject(fto.startDate), [fto.startDate]);

	const claimableFTOInfo = useMemo(() => {
		return {
			amount: fto.statistics.fanTokenTotalAmount,
			endDate: fto.endDate,
			fanTokenOfferingId: fto.id,
			ftoContractAddress: fto.vtoSmartContractAddress,
			logoSrc,
			partnerId: fto.fanToken.partner.id,
			partnerName: fto.fanToken.partner.name,
			tokenContractAddress: fto.fanToken.blockchainAddress || '',
			tokenShortCode: fto.fanToken.shortCode
		};
	}, [
		fto.statistics.fanTokenTotalAmount,
		fto.fanToken.partner.id,
		fto.fanToken.partner.name,
		fto.fanToken.blockchainAddress,
		fto.vtoSmartContractAddress,
		logoSrc,
		fto.id
	]);

	const setClaimableFTOInfo = () => setClaimableFTO(claimableFTOInfo);

	const userHasAlreadyBought = useMemo(
		() => fto.statistics.fanTokenTotalAmount > 0,
		[fto.statistics.fanTokenTotalAmount]
	);

	const hasClaimed = useMemo(() => {
		return fto.claimRequest && fto.claimRequest?.status !== 'failed';
	}, [fto.claimRequest?.status]);

	const isOpened = useMemo(() => fto.status === FTOCardStatus.OPENED, [fto.status]);
	const isPublished = useMemo(() => fto.status === FTOCardStatus.PUBLISHED, [fto.status]);

	const waitingForClaimPeriodToStart = useMemo(() => {
		return (
			userHasAlreadyBought &&
			[FTOCardStatus.FINISHED, FTOCardStatus.CLOSED, FTOCardStatus.LOCKED].includes(fto.status as FTOCardStatus)
		);
	}, [fto.status, userHasAlreadyBought]);

	const delayedClaimPeriod = useMemo(() => {
		return (
			waitingForClaimPeriodToStart && new DateObject(fto.claimStartDate).isBefore(currentTime.add(1, 'second'))
		);
	}, [waitingForClaimPeriodToStart, fto.claimStartDate, currentTime]);

	const hasReachedClaimEndDate = useMemo(() => {
		return new DateObject(fto.claimEndDate).isBefore(currentTime.add(1, 'second'));
	}, [fto.claimEndDate, currentTime]);

	const isClaimable = useMemo(() => {
		return (
			fto.status === FTOCardStatus.CLAIMABLE_PERIOD_ONGOING &&
			userHasAlreadyBought &&
			!hasClaimed &&
			!hasReachedClaimEndDate
		);
	}, [fto.status, userHasAlreadyBought, hasReachedClaimEndDate]);

	const endedClaimPeriod = useMemo(() => {
		return hasReachedClaimEndDate || hasClaimed || fto.status === FTOCardStatus.CLAIMABLE_PERIOD_CLOSED;
	}, [fto.status, hasClaimed, hasReachedClaimEndDate]);

	const isFinished = useMemo(() => {
		return [
			FTOCardStatus.FINISHED,
			FTOCardStatus.CLOSED,
			FTOCardStatus.LOCKED,
			FTOCardStatus.AIRDROP_PROCESSING,
			FTOCardStatus.CLAIMABLE_PERIOD_CLOSED,
			FTOCardStatus.CLAIMABLE_PERIOD_ONGOING
		].includes(fto.status as FTOCardStatus);
	}, [fto.status]);

	const isFinishedWithoutPurchase = useMemo(
		() => !userHasAlreadyBought && isFinished,
		[userHasAlreadyBought, isFinished]
	);

	const handleElapsed = useCallback(() => {
		setStarting(true);
		onElapsed();
	}, [onElapsed, setStarting]);

	const claimableExpirationDate = useMemo(() => {
		const claimEndDateObj = new DateObject(fto.claimEndDate);
		const currentDateObj = new DateObject().add(1, 'second');

		const dateObj = claimEndDateObj < currentDateObj ? currentDateObj : claimEndDateObj;

		return dateObj;
	}, [fto]);

	const claimableStartDate = useMemo(() => new DateObject(fto.claimStartDate), [fto]);

	const ribbonColor = useFanTokenOfferingRibbonColor({
		isFinished,
		status: fto.status as FTOCardStatus
	});

	const almostAmount = useMemo(() => {
		return $t('payments.label.almostAmount', {
			amount: getPeggedAmount(defaultCurrencyId, fto.convertedPrices),
			currency: defaultCurrencyShortCode
		});
	}, [defaultCurrencyId, defaultCurrencyShortCode, fto.convertedPrices]);

	const amount = useMemo(() => {
		return $t('payments.label.amount', {
			amount: getPeggedAmount(altCurrencyId, fto.convertedPrices),
			currency: altCurrencyShortCode
		});
	}, [altCurrencyId, altCurrencyShortCode, fto.convertedPrices]);

	const showClaimButton = useMemo(() => {
		return userHasAlreadyBought && (isClaimable || waitingForClaimPeriodToStart || hasClaimed || endedClaimPeriod);
	}, [userHasAlreadyBought, isClaimable, waitingForClaimPeriodToStart, endedClaimPeriod]);

	useEffect(() => {
		if (isOpened) {
			setStarting(false);
		}
	}, [isOpened]);

	useEffect(() => {
		if (!waitingForClaimPeriodToStart && !isClaimable) {
			return;
		}

		const interval = setInterval(() => {
			setCurrentTime(new DateObject());
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [waitingForClaimPeriodToStart, isClaimable]);

	const parsedStatus = useMemo<FanTokenOfferingCardStatus>(() => {
		if (starting && ftoIndex === 0) return 'aboutToStart';

		if (isPublished) return 'published';

		if (isOpened) return 'opened';

		if (isFinishedWithoutPurchase) return 'finishedWithoutPurchase';

		if (delayedClaimPeriod) return 'delayedClaimPeriod';

		if (waitingForClaimPeriodToStart) return 'waitingForClaimPeriodToStart';

		if (isClaimable) return 'claimable';

		if (endedClaimPeriod) return 'endedClaimPeriod';

		return null;
	}, [
		delayedClaimPeriod,
		endedClaimPeriod,
		ftoIndex,
		isClaimable,
		isFinishedWithoutPurchase,
		isOpened,
		isPublished,
		starting,
		waitingForClaimPeriodToStart
	]);

	return {
		almostAmount,
		amount,
		claimableExpirationDate,
		claimableStartDate,
		handleElapsed,
		hasReachedClaimEndDate,
		isClaimable,
		isFanTokenOfferingHome,
		isOpened,
		logoSrc,
		parsedStatus,
		ribbonColor,
		setClaimableFTOInfo,
		showClaimButton,
		startDate
	};
};
